import { createSlice, PayloadAction, Dispatch } from "@reduxjs/toolkit";
import { Platform } from "interfaces/db";
import { AutoDialerMapping, AutoDialerMappingOptions, UserSelection } from "interfaces/settings";
import { getServicesManager } from "services";
import { SelectionType } from "interfaces/settings";

export interface AutodialerMappingsState {
    // Original mappings from the server
    savedMappings: AutoDialerMapping[];
    // Currently adjusted mappings (dirty state)
    adjustedMappings: AutoDialerMapping[];
    // Available mapping options
    mappingOptions: AutoDialerMappingOptions[];
    // Loading states
    isLoading: boolean;
    isLoaded: boolean;
    isSaving: boolean;
    // Error state
    error: string | null;
    // Has unsaved changes
    hasUnsavedChanges: boolean;
}

const initialState: AutodialerMappingsState = {
    savedMappings: [],
    adjustedMappings: [],
    mappingOptions: [],
    isLoading: false,
    isLoaded: false,
    isSaving: false,
    error: null,
    hasUnsavedChanges: false,
};

export const autodialerMappingsSlice = createSlice({
    name: 'autodialerMappings',
    initialState,
    reducers: {
        // Loading states
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setSaving: (state, action: PayloadAction<boolean>) => {
            state.isSaving = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        // Mappings management
        loadMappings: (state, action: PayloadAction<AutoDialerMapping[]>) => {
            state.savedMappings = action.payload;
            state.adjustedMappings = [...action.payload]; // Deep copy
            state.hasUnsavedChanges = false;
            state.isLoaded = true;
            state.error = null;
        },

        loadMappingOptions: (state, action: PayloadAction<AutoDialerMappingOptions[]>) => {
            state.mappingOptions = action.payload;
            state.error = null;
        },

        addMapping: (state, action: PayloadAction<AutoDialerMapping>) => {
            state.adjustedMappings.push(action.payload);
            state.hasUnsavedChanges = true;
        },

        updateMapping: (state, action: PayloadAction<{
            index: number;
            mapping: AutoDialerMapping;
        }>) => {
            const { index, mapping } = action.payload;
            if (index >= 0 && index < state.adjustedMappings.length) {
                state.adjustedMappings[index] = mapping;
                state.hasUnsavedChanges = true;
            }
        },

        removeMapping: (state, action: PayloadAction<number>) => {
            const index = action.payload;
            if (index >= 0 && index < state.adjustedMappings.length) {
                state.adjustedMappings.splice(index, 1);
                state.hasUnsavedChanges = true;
            }
        },

        updateMappingByPlatform: (state, action: PayloadAction<{
            platform: Platform;
            selection: UserSelection;
            mapping: Partial<AutoDialerMapping>;
        }>) => {
            const { platform, selection, mapping } = action.payload;
            let existingMappingIndex = state.adjustedMappings.findIndex(m => 
                m.platform === platform &&
                m.automation_type === mapping.automation_type &&
                m.trellus_disposition === mapping.trellus_disposition && 
                (
                    (selection.type === SelectionType.ME && m.user_id === selection.id) ||
                    (selection.type === SelectionType.USER && m.locked_user_id === selection.id) ||
                    (selection.type === SelectionType.SUB_TEAM && m.user_group_id === selection.id) ||
                    (selection.type === SelectionType.OVERALL_TEAM && m.team_id === selection.id)
                )
            )
            if (existingMappingIndex !== -1) {
                if (mapping.value === null) state.adjustedMappings.splice(existingMappingIndex, 1);
                else {
                    state.adjustedMappings[existingMappingIndex] = {
                        ...state.adjustedMappings[existingMappingIndex],
                        ...mapping
                    }
                }
            } else {
                if (mapping.value === null) return;
                state.adjustedMappings.push({
                    user_id: selection.type === SelectionType.ME ? selection.id : "",
                    user_group_id: selection.type === SelectionType.SUB_TEAM ? selection.id : "",
                    team_id: selection.type === SelectionType.OVERALL_TEAM ? selection.id : "",
                    locked_user_id: selection.type === SelectionType.USER ? selection.id : "",
                    platform,
                    ...mapping
                } as AutoDialerMapping);
            }
            state.hasUnsavedChanges = true;
        },

        saveMappingsSuccess: (state) => {
            state.savedMappings = [...state.adjustedMappings];
            state.hasUnsavedChanges = false;
            state.isSaving = false;
            state.error = null;
        },

        saveMappingsFailure: (state, action: PayloadAction<string>) => {
            state.isSaving = false;
            state.error = action.payload;
        },

        resetAdjustedMappings: (state) => {
            state.adjustedMappings = [...state.savedMappings];
            state.hasUnsavedChanges = false;
        },

        clearError: (state) => {
            state.error = null;
        },
    },
});

export const {
    setLoading,
    setSaving,
    setError,
    loadMappings,
    loadMappingOptions,
    addMapping,
    updateMapping,
    removeMapping,
    updateMappingByPlatform,
    saveMappingsSuccess,
    saveMappingsFailure,
    resetAdjustedMappings,
    clearError,
} = autodialerMappingsSlice.actions;

// Async thunks
export const loadAutodialerMappings = (dispatch: Dispatch) => {
    dispatch(setLoading(true));
    return Promise.all([
        getServicesManager().getAllAutodialerMappings(),
        getServicesManager().getAutodialerMappingOptions()
    ])
    .then(([mappings, options]) => {
        dispatch(loadMappings(mappings || []));
        dispatch(loadMappingOptions(options || []));
        dispatch(setLoading(false));
    })
    .catch(error => {
        dispatch(setError(error.message || 'Failed to load mappings'));
        dispatch(setLoading(false));
    });
};

export default autodialerMappingsSlice.reducer; 